import React, { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import ProductScoutStore from '../ProductScout.store'
import UserProfileService from '../../../shared/services/UserProfile.service'
import { HOUSEHOLD } from '../WizardPane/wizardOptions'
import './ChatPane.component.scss'

/**
 * `ChatPane` — the active-conversation surface inside the ProductScout tab.
 * Rendered when `ProductScoutStore.is_active === true`; replaced by
 * `WizardPane` when no conversation is active.
 *
 * Composition:
 *   - Toolbar: New Conversation button (archives the current thread and
 *     returns the user to the wizard)
 *   - ToolProgress: shows when `pending_tool_calls.length > 0` (LLM is
 *     fetching corpus chunks or looking up state availability mid-response)
 *   - Messages: scrollable list of role-styled bubbles (user / assistant /
 *     tool / system) with optional inline Citations toggle per bubble
 *   - Compose: textarea + send button. Disabled while streaming so the
 *     user cannot pile up messages mid-response
 *
 * Streaming behavior: while `is_streaming === true` the last assistant
 * bubble shows a typing-dots indicator at the end of its growing content.
 * In Slice 1 the streaming is simulated by `ProductScoutStore.sendMessage`
 * via setInterval; Slice 3 replaces the local sim with Reverb token events
 * but the UI here doesn't change.
 */
function ChatPane() {
  // sortedMessages (not .messages) — the store's underlying array can be
  // mutated out-of-order by Reverb delta events that race the HTTP
  // response. The getter sorts by server-assigned id (auto-increment)
  // so the chronological invariant "newest at the bottom" holds no
  // matter which event arrives first.
  const messages = ProductScoutStore.sortedMessages
  const isStreaming = ProductScoutStore.is_streaming
  const isSubmitting = ProductScoutStore.is_submitting
  const isOpening = ProductScoutStore.is_opening
  const pendingTools = ProductScoutStore.pending_tool_calls
  const budgetBlocked = ProductScoutStore.budget_blocked
  // Observed so a service error during sendMessage surfaces in the UI.
  // Before the G7 refactor this arrived as a thrown exception caught in
  // handleSend; now it's set on the store and we observe it directly to
  // avoid the same dead-catch regression that bit WizardPane.
  const storeError = ProductScoutStore.error
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null
  const lastMessageContent = lastMessage?.content || ''
  // Compose is locked while the service round-trip is in flight, tokens are
  // streaming into the latest bubble, OR the user has hit their ProductScout
  // budget (G7). Typing-dots indicator only rides on the streaming bubble.
  const composeLocked = isSubmitting || isStreaming || isOpening || !!budgetBlocked

  const [draft, setDraft] = useState('')
  const messagesRef = useRef(null)

  // Auto-scroll to bottom on new message arrival OR streaming content tick.
  // The store rebuilds the messages array on each streaming character, so
  // `lastMessageContent` changes ~30ms during a streamed reply; this keeps
  // the latest content in view without the user having to scroll manually.
  useEffect(() => {
    const el = messagesRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages.length, lastMessageContent, isStreaming, isOpening])

  const onComposeKeyDown = e => {
    // Enter sends, Shift+Enter inserts a newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleSend = async () => {
    const content = draft.trim()
    if (!content || composeLocked) return
    // Clear any stale store-side error before the next attempt so an old
    // banner doesn't linger across sends. `sendMessage` (post-G7 refactor)
    // returns null on error and sets `store.error` or `store.budget_blocked`
    // — the UI feedback is rendered from those observed values, not a
    // thrown exception.
    ProductScoutStore.clearError()
    setDraft('')
    await ProductScoutStore.sendMessage(content)
  }

  const sendSuggestion = async question => {
    if (composeLocked) return
    ProductScoutStore.clearError()
    await ProductScoutStore.sendMessage(question)
  }

  const handleNewConversation = () => {
    ProductScoutStore.archiveConversation()
  }

  return (
    <div className='ChatPane'>
      <div className='ChatPane__toolbar'>
        <button type='button' className='ChatPane__new' onClick={handleNewConversation}>
          + New conversation
        </button>
      </div>

      {budgetBlocked && (
        <BudgetBlockBanner block={budgetBlocked} onDismiss={() => ProductScoutStore.clearBudgetBlock()} />
      )}

      <ToolProgress calls={pendingTools} />

      <div
        ref={messagesRef}
        className='ChatPane__messages'
        role='log'
        aria-live='polite'
        aria-label='ProductScout conversation'
      >
        {messages.length === 0 && !isOpening && (
          <EmptyConversationHint onSuggestion={composeLocked ? null : sendSuggestion} />
        )}
        {messages.length === 0 && isOpening && <OpeningBubble />}
        {messages.map(m => {
          const isLast = lastMessage && m.id === lastMessage.id
          const showTypingDots = isStreaming && isLast && m.role === 'assistant'
          // Suggested follow-ups are clickable only on the latest reply and
          // only while the compose box is free.
          const onSuggestion = isLast && !composeLocked ? sendSuggestion : null
          return <MessageBubble key={m.id} message={m} showTypingDots={showTypingDots} onSuggestion={onSuggestion} />
        })}
      </div>

      <form
        className='ChatPane__compose'
        onSubmit={e => {
          e.preventDefault()
          handleSend()
        }}
      >
        {!budgetBlocked && storeError && (
          <div className='ChatPane__send-error' role='alert'>
            {storeError}
            <button
              type='button'
              className='ChatPane__send-error-dismiss'
              onClick={() => ProductScoutStore.clearError()}
              aria-label='Dismiss error'
            >
              ×
            </button>
          </div>
        )}
        <textarea
          className='ChatPane__compose-input'
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={onComposeKeyDown}
          rows={2}
          placeholder={
            budgetBlocked
              ? 'ProductScout budget reached — compose disabled.'
              : isOpening
              ? 'ProductScout is reviewing options for this prospect…'
              : composeLocked
              ? 'Waiting for response…'
              : 'Ask about carrier products, eligibility, or state availability…'
          }
          disabled={composeLocked}
          aria-label='Message ProductScout'
        />
        <button
          type='submit'
          className='ChatPane__compose-send'
          disabled={composeLocked || draft.trim().length === 0}
          aria-busy={isSubmitting || isStreaming}
        >
          {isSubmitting ? (
            <>
              <span className='ChatPane__compose-spinner' aria-hidden='true' />
              Sending…
            </>
          ) : isStreaming ? (
            <>
              <span className='ChatPane__compose-spinner' aria-hidden='true' />
              Thinking…
            </>
          ) : (
            'Send'
          )}
        </button>
      </form>
    </div>
  )
}

/**
 * Placeholder assistant bubble while the intake-driven opening reply is
 * generating, so the agent sees ProductScout is already working.
 */
function OpeningBubble() {
  return (
    <div className='ChatPane__bubble ChatPane__bubble--assistant' role='status'>
      <div className='ChatPane__bubble-meta'>
        <span className='ChatPane__bubble-role'>{ASSISTANT_NAME}</span>
      </div>
      <div className='ChatPane__bubble-content'>
        Reviewing options for this prospect
        <span className='ChatPane__typing-dots' aria-hidden='true'>
          <span />
          <span />
          <span />
        </span>
      </div>
    </div>
  )
}

/**
 * Starter questions for the static welcome — profile-aware when the agent
 * filled in the intake, general otherwise.
 */
const STARTERS_WITH_PROFILE = [
  'What are the best-fit options for this prospect?',
  'Compare major medical and short-term plans for them',
  'What supplemental coverage pairs well?',
]
const STARTERS_GENERAL = [
  'Which carriers offer major medical plans?',
  'What short-term medical options are available in Florida?',
  'What hospital indemnity plans do we offer?',
]

/**
 * Static welcome shown when a conversation has no messages: the opening
 * reply is off, failed, or hasn't been requested. Never a blank pane —
 * it says what ProductScout covers and offers clickable starters.
 */
function EmptyConversationHint({ onSuggestion }) {
  const intake = ProductScoutStore.intake || {}
  const summary = formatIntakeSummary(intake)
  // Admin-managed starters (Nova → Wizard Options), else the built-in ones.
  const managed = ProductScoutStore.wizard_options?.starters
  const starters = summary
    ? managed?.with_profile?.length
      ? managed.with_profile
      : STARTERS_WITH_PROFILE
    : managed?.general?.length
    ? managed.general
    : STARTERS_GENERAL
  return (
    <div className='ChatPane__empty-hint' role='status'>
      <div className='ChatPane__empty-hint-heading'>Conversation started.</div>
      {summary && <div className='ChatPane__empty-hint-summary'>{summary}</div>}
      <div className='ChatPane__empty-hint-prompt'>
        Ask about carriers, plan details, eligibility, state availability, or family and cost-of-coverage questions.
      </div>
      <div className='ChatPane__suggestions' aria-label='Suggested questions'>
        {starters.map(q => (
          <button
            key={q}
            type='button'
            className='ChatPane__suggestion'
            onClick={() => onSuggestion && onSuggestion(q)}
            disabled={!onSuggestion}
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * Produce a short human-readable line from the wizard intake (gender,
 * age_band, tobacco, state, household, health). Returns null when the
 * intake is empty so the hint falls back to the generic prompt only.
 */
function formatIntakeSummary(intake) {
  const parts = []
  if (intake.gender) {
    parts.push(intake.gender.charAt(0).toUpperCase() + intake.gender.slice(1))
  }
  if (intake.age_band) parts.push(intake.age_band)
  if (intake.tobacco === false) parts.push('non-tobacco')
  else if (intake.tobacco === true) parts.push('tobacco')
  if (intake.state) parts.push(intake.state)
  const householdOptions = ProductScoutStore.wizard_options?.household?.length
    ? ProductScoutStore.wizard_options.household
    : HOUSEHOLD
  const household = householdOptions.find(h => h.value === intake.household)
  if (household) parts.push(household.label.toLowerCase())
  if (parts.length === 0) return null
  // No conditions selected means healthy (see wizardOptions CONDITIONS).
  const conditionCount = Array.isArray(intake.conditions) ? intake.conditions.length : 0
  parts.push(conditionCount ? `${conditionCount} health condition${conditionCount > 1 ? 's' : ''}` : 'healthy')
  return parts.join(' · ')
}

/** Speaker name on assistant bubbles. */
const ASSISTANT_NAME = 'ProductScout Agent'

/**
 * Speaker label for a bubble: the agent's first name on their own messages
 * (only the conversation's owner can view it), "ProductScout Agent" on
 * replies, the capitalized role otherwise.
 */
function bubbleLabel(role) {
  if (role === 'user') return UserProfileService.get('u_fname') || 'You'
  if (role === 'assistant') return ASSISTANT_NAME
  return role.charAt(0).toUpperCase() + role.slice(1)
}

/**
 * Per-message compliance footer text. Required on every assistant message
 * per the ProductScout user manual ("Privacy & Compliance" section) and
 * ADR-007. Hidden when the assistant bubble is empty (streaming hasn't
 * produced visible content yet) so it does not flash above the typing dots.
 */
const ASSISTANT_FOOTER = 'Informational only. Not a quote. Verify with the carrier brochure before binding.'

/**
 * `BudgetBlockBanner` — surfaces a HTTP 429 PRODUCTSCOUT_BUDGET_BLOCKED
 * response distinctly from generic errors. The compose area is disabled
 * while this banner is visible; clicking Dismiss reopens it with the
 * understanding that the next call will likely re-block if the budget
 * hasn't reset server-side. Reset time is server-truthful — we render
 * what the backend reports.
 *
 * @param {Object} props
 * @param {{scope:'daily'|'monthly', reset_at:?string, message:string}} props.block
 * @param {() => void} props.onDismiss
 */
function BudgetBlockBanner({ block, onDismiss }) {
  const scopeLabel = block.scope === 'monthly' ? 'monthly' : 'daily'
  const resetLabel = block.reset_at ? ` Resets ${formatResetTime(block.reset_at)}.` : ''
  return (
    <div className='ChatPane__budget-block' role='alert'>
      <div className='ChatPane__budget-block-body'>
        <strong>{`ProductScout ${scopeLabel} limit reached.`}</strong>
        <span className='ChatPane__budget-block-detail'>
          {block.message}
          {resetLabel}
        </span>
      </div>
      <button
        type='button'
        className='ChatPane__budget-block-dismiss'
        onClick={onDismiss}
        aria-label='Dismiss budget-block banner'
      >
        Dismiss
      </button>
    </div>
  )
}

/**
 * Format a server-provided ISO reset timestamp. Falls back to the raw
 * string when parsing fails so the banner still reads correctly with an
 * unexpected backend shape.
 */
function formatResetTime(iso) {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      month: 'short',
      day: 'numeric',
    })
  } catch (e) {
    return iso
  }
}

/**
 * `MessageBubble` — one row in the message timeline. Role drives the visual
 * style (user right-aligned blue, assistant left-aligned gray, tool inline
 * small italic, system muted centered).
 *
 * Assistant bubbles carry a small compliance footer ("Informational only…")
 * and, when the server attaches a `citations` array, a "Show sources"
 * toggle. Citations are hidden by default per the user manual's "Click
 * Show citations to expand citations under each message" requirement.
 *
 * @param {Object} props
 * @param {Object} props.message
 * @param {boolean} props.showTypingDots - When true, append an animated
 *   typing-dots indicator after the bubble content (used on the last
 *   assistant message while `is_streaming === true`).
 * @param {?function(string)} props.onSuggestion - Sends a suggested
 *   follow-up; null hides the suggestion chips' click handling.
 */
function MessageBubble({ message, showTypingDots, onSuggestion }) {
  const [citationsOpen, setCitationsOpen] = useState(false)
  const isAssistant = message.role === 'assistant'
  const hasCitations = Array.isArray(message.citations) && message.citations.length > 0
  const hasVisibleContent = !!message.content && message.content.length > 0
  // Show the compliance footer on assistant bubbles, but only once the
  // assistant has produced any visible content. (Skipping the footer on the
  // empty placeholder avoids flashing it above the typing dots.)
  const showAssistantFooter = isAssistant && hasVisibleContent
  const roleLabel = bubbleLabel(message.role)

  return (
    <div className={`ChatPane__bubble ChatPane__bubble--${message.role}`}>
      <div className='ChatPane__bubble-meta'>
        <span className='ChatPane__bubble-role'>{roleLabel}</span>
        {message.created_at && <span className='ChatPane__bubble-time'>{formatTime(message.created_at)}</span>}
      </div>
      <div className={`ChatPane__bubble-content${isAssistant ? ' ChatPane__bubble-content--markdown' : ''}`}>
        {isAssistant ? <AssistantContent content={message.content} onSuggestion={onSuggestion} /> : message.content}
        {showTypingDots && (
          <span className='ChatPane__typing-dots' aria-hidden='true'>
            <span />
            <span />
            <span />
          </span>
        )}
      </div>
      {showAssistantFooter && (
        <div className='ChatPane__bubble-compliance' aria-label='ProductScout compliance disclaimer'>
          {ASSISTANT_FOOTER}
        </div>
      )}
      {hasCitations && (
        <div className='ChatPane__bubble-footer'>
          <button
            type='button'
            className='ChatPane__citations-toggle'
            onClick={() => setCitationsOpen(v => !v)}
            aria-expanded={citationsOpen}
          >
            {citationsOpen ? 'Hide' : 'Show'} sources ({message.citations.length})
          </button>
          {citationsOpen && (
            <ol className='ChatPane__citations'>
              {message.citations.map((c, i) => (
                <li key={`${c.doc_id}-${c.page}-${i}`} className='ChatPane__citation'>
                  <span className='ChatPane__citation-ref'>
                    #{c.doc_id}·p{c.page}
                  </span>
                  <span className='ChatPane__citation-excerpt'>{c.excerpt}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Assistant replies are Markdown (headings, bold, lists, GFM tables). Raw
 * HTML stays disabled — this is model output. Links open in a new tab and
 * tables scroll sideways inside the narrow sidebar.
 *
 * A trailing "Suggested questions:" block (see the per-turn response rules
 * in LLMOnlyStrategy) is lifted out of the Markdown and shown as chips.
 */
function AssistantContent({ content, onSuggestion }) {
  const { body, suggestions } = splitSuggestions(content || '')
  return (
    <>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
        {body}
      </ReactMarkdown>
      {suggestions.length > 0 && (
        <div className='ChatPane__suggestions' aria-label='Suggested questions'>
          {suggestions.map(q => (
            <button
              key={q}
              type='button'
              className='ChatPane__suggestion'
              onClick={() => onSuggestion && onSuggestion(q)}
              disabled={!onSuggestion}
            >
              {q}
            </button>
          ))}
        </div>
      )}
    </>
  )
}

const MARKDOWN_COMPONENTS = {
  a: ({ node, ...props }) => <a {...props} target='_blank' rel='noopener noreferrer' />,
  table: ({ node, ...props }) => (
    <div className='ChatPane__table-wrap'>
      <table {...props} />
    </div>
  ),
}

/**
 * Split a reply into its Markdown body and the list items of a trailing
 * "Suggested questions:" block (heading may be bold; items may be bulleted
 * or numbered). Returns the content unchanged when no block is present.
 */
function splitSuggestions(content) {
  const lines = content.split('\n')
  let start = -1
  for (let i = lines.length - 1; i >= 0; i--) {
    if (/^\s*(#+\s*)?(\*\*)?\s*suggested (follow-up )?questions\s*:?\s*(\*\*)?\s*:?\s*$/i.test(lines[i])) {
      start = i
      break
    }
  }
  if (start === -1) return { body: content, suggestions: [] }

  const suggestions = lines
    .slice(start + 1)
    .map(l => l.match(/^\s*(?:[-*•]|\d+[.)])\s+(.+?)\s*$/))
    .filter(Boolean)
    .map(m => m[1].replace(/\*\*/g, '').replace(/^["“]|["”]$/g, '').trim())
    .filter(q => q.length > 0)
    .slice(0, 4)

  const body = lines
    .slice(0, start)
    .join('\n')
    .replace(/\n\s*(---|\*\*\*)\s*$/, '')
    .trimEnd()
  return { body, suggestions }
}

/**
 * `ToolProgress` — small inline banner shown when the LLM has emitted one
 * or more tool calls that haven't yet returned. Drives the "looking up…"
 * affordance from the user manual. Slice 1 leaves `pending_tool_calls`
 * empty in the mock; Slice 3 wires it up via Reverb `productscout.tool_started`
 * / `productscout.tool_finished` events.
 */
function ToolProgress({ calls }) {
  if (!Array.isArray(calls) || calls.length === 0) return null
  const label =
    calls.length === 1 ? `Looking up ${humaniseToolName(calls[0].tool)}…` : `Looking up ${calls.length} sources…`
  return (
    <div className='ChatPane__tool-progress' role='status' aria-live='polite'>
      <span className='ChatPane__tool-dot' aria-hidden='true' />
      {label}
    </div>
  )
}

/**
 * Map a tool's snake_case name to a friendly noun phrase for the
 * `ToolProgress` indicator. Falls back to the raw tool name if unknown.
 */
function humaniseToolName(name) {
  switch (name) {
    case 'SearchCorpus':
      return 'carrier brochures'
    case 'GetCarrier':
      return 'carrier info'
    case 'CiteSource':
      return 'sources'
    default:
      return name
  }
}

/**
 * Compact timestamp formatter for message metadata — shows time of day
 * (e.g. "2:34 PM") since most conversations happen in a single session.
 */
function formatTime(iso) {
  try {
    const d = new Date(iso)
    return d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch (e) {
    return ''
  }
}

export default observer(ChatPane)
